import * as React from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge, Typography, Box, useTheme, Tooltip } from "@mui/material";
import { isAnimalActive, selectVisibleAnimalCount } from "utils/animalUtils";
import { DEFAULT_RECENT_WINDOW_DAYS } from "utils/suspiciousAnimals";
import { isTreatmentCompleted } from "utils/treatmentStatus";

import "./AnimalsCard.css";

const debugLog = () => {};

/**
 * Base Animal Card component to create consistent cards with shared functionality
 */
const AnimalCard = (props) => {
	const {
		title,
		count,
		status = "default",
		navigateTo,
		navigateState,
		badgeContent,
		badgeColor = "error",
		onClick,
		onBadgeClick,
		cardId,
		pulsatingBadge = false,
		badgeTooltip = "",
	} = props;

        const navigate = useNavigate();
	const theme = useTheme();

	const handleClick = () => {
		if (onClick) {
			onClick();
                } else if (navigateTo) {
                        if (navigateState) {
                                navigate(navigateTo, { state: navigateState });
                        } else if (typeof navigateTo === "string") {
                                navigate(navigateTo);
                        } else if (
                                navigateTo &&
                                typeof navigateTo === "object" &&
                                "pathname" in navigateTo
                        ) {
                                const { pathname, state } = navigateTo;
                                navigate(pathname, state ? { state } : undefined);
                        }
                }
        };

	const handleBadgeClick = (e) => {
		e.stopPropagation(); // Prevent card click when badge is clicked
		if (onBadgeClick) {
			onBadgeClick();
		}
	};

	// Define consistent styling for different status types
	const getStatusColor = () => {
		switch (status) {
			case "green":
				return theme.palette.success.main;
			case "yellow":
				return theme.palette.warning.main;
			case "red":
				return theme.palette.error.main;
			default:
				return theme.palette.text.primary;
		}
	};

	return (
		<Card
			className="animals-card"
			data-card-id={cardId}
			sx={{
				position: "relative",
				transition: "transform 0.2s, box-shadow 0.3s ease-in-out",
				cursor: navigateTo || onClick ? "pointer" : "default",
				"&:hover": {
					transform: navigateTo || onClick ? "scale(1.05)" : "none",
					boxShadow: "0 8px 16px rgba(0, 0, 0, 0.2)",
				},
			}}
			onClick={handleClick}
		>
			{badgeContent && (
				<Tooltip title={badgeTooltip} arrow placement="top">
					<Badge
						badgeContent={badgeContent}
						color={badgeColor}
						onClick={handleBadgeClick}
						sx={{
							position: "absolute",
							top: { xs: 8, sm: 12, md: 15 },
							left: { xs: 8, sm: 12, md: 15 },
							transition: "transform 0.2s",
							cursor: onBadgeClick ? "pointer" : "default",
							"&:hover": {
								transform: onBadgeClick ? "scale(1.05)" : "none",
							},
							animation: pulsatingBadge
								? "pulsate 1.5s infinite alternate"
								: "none",
							"@keyframes pulsate": {
								"0%": {
									transform: "scale(1)",
									opacity: 1,
								},
								"50%": {
									transform: "scale(1.1)",
									opacity: 0.8,
								},
								"100%": {
									transform: "scale(1)",
									opacity: 1,
								},
							},
						}}
					/>
				</Tooltip>
			)}

			<CardContent sx={{ 
				padding: { xs: 2, sm: 2.5, md: 3 },
				display: "flex",
				flexDirection: "column",
				justifyContent: "center",
				alignItems: "center",
				height: "100%",
			}}>
				<Typography
					variant="h6"
					sx={{
						fontWeight: 500,
						marginBottom: { xs: 0.5, md: 1 },
						fontSize: { xs: "0.875rem", sm: "0.9375rem", md: "1rem" },
					}}
				>
					{title}
				</Typography>
				<Box sx={{ mt: { xs: 1, md: 2 } }}>
					<Typography
						variant="h3"
						sx={{
							fontWeight: "bold",
							color: getStatusColor(),
							fontSize: { xs: "1.75rem", sm: "2rem", md: "2.5rem" },
							lineHeight: 1.2,
						}}
					>
						{count}
					</Typography>
				</Box>
			</CardContent>
		</Card>
	);
};

// Card ID constants for settings
export const CARD_IDS = {
	ACTIVE_ANIMALS: "active-animals",
	REGISTERED_ANIMALS: "registered-animals",
	SUSPICIOUS_ANIMALS: "suspicious-animals",
	RELUCTANT_ANIMALS: "reluctant-animals",
	OPEN_TREATMENTS: "open-treatments",
	TOTAL_TASKS: "total-tasks",
	PENDING_TASKS: "pending-tasks",
	IN_PROGRESS_TASKS: "in-progress-tasks",
	COMPLETED_TASKS: "completed-tasks",
	NODE_STATISTICS: "node-statistics",
	TREATMENT_STATISTICS: "treatment-statistics",
	ERP_SETTINGS: "erp-settings",
};

// Default card visibility settings
export const DEFAULT_CARD_SETTINGS = {
	[CARD_IDS.ACTIVE_ANIMALS]: true,
	[CARD_IDS.REGISTERED_ANIMALS]: true,
	[CARD_IDS.SUSPICIOUS_ANIMALS]: true,
	[CARD_IDS.RELUCTANT_ANIMALS]: true,
	[CARD_IDS.OPEN_TREATMENTS]: true,
	[CARD_IDS.TOTAL_TASKS]: true,
	[CARD_IDS.PENDING_TASKS]: true,
	[CARD_IDS.IN_PROGRESS_TASKS]: true,
	[CARD_IDS.COMPLETED_TASKS]: true,
	[CARD_IDS.NODE_STATISTICS]: true,
	[CARD_IDS.TREATMENT_STATISTICS]: true,
	[CARD_IDS.ERP_SETTINGS]: true,
};

// Card display info for settings UI
export const CARD_DISPLAY_INFO = [
	{
		id: CARD_IDS.ACTIVE_ANIMALS,
		label: "card_settings.active",
		description: "card_settings.active_description",
	},
	{
		id: CARD_IDS.REGISTERED_ANIMALS,
		label: "card_settings.registered",
		description: "card_settings.registered_description",
	},
	{
		id: CARD_IDS.SUSPICIOUS_ANIMALS,
		label: "card_settings.suspicious",
		description: "card_settings.suspicious_description",
	},
	{
		id: CARD_IDS.RELUCTANT_ANIMALS,
		label: "card_settings.reluctant",
		description: "card_settings.reluctant_description",
	},
	{
		id: CARD_IDS.OPEN_TREATMENTS,
		label: "card_settings.open_treatments",
		description: "card_settings.open_treatments_description",
	},
	{
		id: CARD_IDS.TOTAL_TASKS,
		label: "card_settings.total_tasks",
		description: "card_settings.total_tasks_description",
	},
	{
		id: CARD_IDS.PENDING_TASKS,
		label: "card_settings.pending_tasks",
		description: "card_settings.pending_tasks_description",
	},
	{
		id: CARD_IDS.IN_PROGRESS_TASKS,
		label: "card_settings.in_progress_tasks",
		description: "card_settings.in_progress_tasks_description",
	},
	{
		id: CARD_IDS.COMPLETED_TASKS,
		label: "card_settings.completed_tasks",
		description: "card_settings.completed_tasks_description",
	},
	{
		id: CARD_IDS.NODE_STATISTICS,
		label: "card_settings.node_statistics",
		description: "card_settings.node_statistics_description",
	},
	{
		id: CARD_IDS.TREATMENT_STATISTICS,
		label: "card_settings.treatment_statistics",
		description: "card_settings.treatment_statistics_description",
	},
	{
		id: CARD_IDS.ERP_SETTINGS,
		label: "card_settings.erp_settings",
		description: "card_settings.erp_settings_description",
	},
];

/**
 * Card displaying the count of active animals
 */
export function ActiveAnimalCard({ animals = [] }) {
        const { t } = useTranslation();
        const navigate = useNavigate();

	const activeAnimals = animals.filter(
		(animal) => isAnimalActive(animal)
	).length;

	// Commented out lost animals functionality
	// const lostAnimals = animals.filter((animal) => animal.Lost).length;

	// Navigate to all animals
	const handleCardClick = () => {
                navigate("/animals");
	};

	// Navigate to suspicious animals when badge is clicked
	// Commented out badge click handler
	// const handleBadgeClick = () => {
	// 	history.push({
	// 		pathname: "/animals",
	// 		state: { state: "suspicious" },
	// 	});
	// };

	return (
		<AnimalCard
			title={t("active")}
			count={activeAnimals}
			status="default"
			onClick={handleCardClick}
			// Commented out all badge-related props
			// badgeContent={lostAnimals > 0 ? lostAnimals : null}
			// badgeColor="error"
			// onBadgeClick={lostAnimals > 0 ? handleBadgeClick : undefined}
			// pulsatingBadge={lostAnimals > 0}
			// badgeTooltip={lostAnimals > 0 ? t("lost_eartags_tooltip") : ""}
			cardId={CARD_IDS.ACTIVE_ANIMALS}
		/>
	);
}

/**
 * Card displaying the count of registered animals
 */
export function InactiveAnimalCard({ animals = [] }) {
	const { t } = useTranslation();

	return (
		<AnimalCard
			title={t("registered")}
			// Shared herd-count rule (see utils/animalUtils): the header metric
			// bar excluded archived animals here while this tile did not, so the
			// two headline numbers disagreed on the same screen.
			count={selectVisibleAnimalCount(animals)}
			status="default"
			cardId={CARD_IDS.REGISTERED_ANIMALS}
		/>
	);
}

/**
 * Card displaying the count of suspicious animals
 */
export function SuspiciousAnimals({
        events = [],
        animals = [],
        treatments = [],
        navigateState,
}) {
        const { t } = useTranslation();
        const navigate = useNavigate();

	// The 'events' prop actually contains the filtered suspicious animals
	// We'll use it directly as the count
	const suspiciousCount = events.length;

	// Debug logging
	debugLog("SuspiciousAnimals:", {
		receivedCount: events.length,
		totalAnimals: animals.length,
		totalTreatments: treatments.length,
	});

	const handleClick = () => {
                navigate("/animals", { state: navigateState || { state: "suspicious" } });
        };

	return (
                <Tooltip
                        title={t("suspicious-filter-rule-tooltip", {
                                days: DEFAULT_RECENT_WINDOW_DAYS,
                        })}
                        arrow
                        placement="top"
                >
                        <Box component="span" sx={{ display: "block" }}>
                                <AnimalCard
                                        title={t("suspicious")}
                                        count={suspiciousCount}
                                        status={suspiciousCount === 0 ? "green" : "yellow"}
                                        onClick={handleClick}
                                        cardId={CARD_IDS.SUSPICIOUS_ANIMALS}
                                />
                        </Box>
                </Tooltip>
	);
}

/**
 * Helper function to get unique predictions by animal and type
 * @param {Array} events - Array of prediction events
 * @param {String} type - The type of prediction to filter for (optional)
 * @returns {Array} - Array of unique predictions (one per animal per type)
 */
const getUniquePredictions = (events, type = null) => {
        const uniquePredictions = [];

	if (!events || !Array.isArray(events)) {
		return [];
	}

	events.forEach((prediction) => {
		// Skip if we're filtering by type and this prediction doesn't match
		if (type && prediction.Type !== type) {
			return;
		}

		// Use composite key of MAC+Type to allow one animal to have multiple prediction types
		const key = `${prediction.MAC}-${prediction.Type}`;
		const index = uniquePredictions.findIndex(
			(element) => `${element.MAC}-${element.Type}` === key
		);

		if (index === -1) {
			uniquePredictions.push(prediction);
		} else if (uniquePredictions[index].t < prediction.t) {
			uniquePredictions[index] = prediction;
		}
	});

	return uniquePredictions;
};

/**
 * Card displaying the count of reluctant/lame animals
 */
export function AnimalReluctant({ events = [], animals = [] }) {
        const { t } = useTranslation();
        const navigate = useNavigate();

	// Debug logs
	debugLog("AnimalReluctant received events:", events ? events.length : 0);
	if (events && events.length > 0) {
		const types = {};
		events.forEach((event) => {
			if (event.Type) {
				types[event.Type] = (types[event.Type] || 0) + 1;
			}
		});
		debugLog("AnimalReluctant event types:", types);
	}

	// Count lame animals directly from the animals array
	const numberOfLameAnimals = animals.filter(
		(animal) => animal.Lame === true || animal.Lame === 1
	).length;

	// Get health/lameness predictions, including LAHM type
	const healthPredictions = getUniquePredictions(events).filter(
		(event) =>
			event.Type === "Health" ||
			event.Type === "Lameness" ||
			event.Type === "LAHM"
	);

	debugLog(
		"Filtered Health/Lameness/LAHM predictions:",
		healthPredictions.length
	);
	if (healthPredictions.length > 0) {
		debugLog(
			"Health prediction types:",
			healthPredictions.map((p) => p.Type)
		);
	}

	const numberOfHealthIssues = healthPredictions.length;

	// Use the higher count between direct animal flags and predictions
	const numberOfReluctantAnimals = Math.max(
		numberOfLameAnimals,
		numberOfHealthIssues
	);

        const handleClick = () => {
                navigate("/predictions");
        };

	return (
		<AnimalCard
			title={t("reluctant")}
			count={numberOfReluctantAnimals}
			status={numberOfReluctantAnimals === 0 ? "green" : "red"}
			onClick={handleClick}
			cardId={CARD_IDS.RELUCTANT_ANIMALS}
		/>
	);
}

/**
 * Card displaying the count of open treatments
 */
export function OpenTreatments({ treatments = [] }) {
        const { t } = useTranslation();
        const navigate = useNavigate();

	// Debug treatments data
	debugLog(
		"OpenTreatments - received treatments:",
		treatments ? treatments.length : 0
	);
	if (treatments && treatments.length > 0) {
		debugLog("OpenTreatments - first treatment sample:", treatments[0]);
		debugLog(
			"OpenTreatments - treatment fields:",
			Object.keys(treatments[0])
		);
	}

	const openTreatments = treatments.filter(
		(treatment) => !isTreatmentCompleted(treatment)
	).length;

	debugLog(
		`OpenTreatments - openTreatments count: ${openTreatments} of ${treatments.length}`
	);

        const handleClick = () => {
                navigate("/treatments", { state: { state: "suspicious" } });
        };

	return (
		<AnimalCard
			title={t("open-treatments")}
			count={openTreatments}
			status={openTreatments === 0 ? "green" : "red"}
			onClick={handleClick}
			cardId={CARD_IDS.OPEN_TREATMENTS}
		/>
	);
}

/**
 * Card displaying the count of total work tasks
 */
export function TotalTasksCard({ workTasks = [] }) {
        const { t } = useTranslation();
        const navigate = useNavigate();

	// Add debugging to see what's in workTasks
	debugLog("TotalTasksCard - workTasks:", workTasks);

	// Check if any tasks have assignedToName property
	let hasAssignedToNameProp = false;
	if (workTasks.length > 0) {
		hasAssignedToNameProp = workTasks.some((task) => "assignedToName" in task);
		debugLog("Has assignedToName property:", hasAssignedToNameProp);
	}

	// Filter tasks that have an assignedToName property
	const assignedTasks = workTasks.filter((task) => {
		// Check if task is an object
		if (typeof task !== "object" || task === null) {
			return false;
		}

		// Check for assignedToName property directly or in assignedTo object
		return (
			task.assignedToName ||
			(task.assignedTo && task.assignedTo.name) ||
			task.assignedToName === ""
		);
	});

	debugLog("TotalTasksCard - filtered assignedTasks:", assignedTasks);
	const totalTasks = assignedTasks.length;

        const handleClick = () => {
                navigate("/workplaner");
        };

	return (
		<AnimalCard
			title={t("total-tasks")}
			count={totalTasks}
			status="default"
			onClick={handleClick}
			cardId={CARD_IDS.TOTAL_TASKS}
		/>
	);
}

/**
 * Card displaying the count of pending work tasks
 */
export function PendingTasksCard({ workTasks = [] }) {
        const { t } = useTranslation();
        const navigate = useNavigate();

	// Debug workTasks and status values
	debugLog("PendingTasksCard - workTasks:", workTasks);

	// Filter tasks that are assigned and pending
	const pendingTasks = workTasks.filter((task) => {
		// Check if task is an object
		if (typeof task !== "object" || task === null) {
			return false;
		}

		// Check for assignedToName property
		const hasAssignedName = Boolean(
			task.assignedToName || (task.assignedTo && task.assignedTo.name)
		);

		// Check various formats of 'pending' status
		let isPending = false;

		// Check for status property directly
		if ("status" in task) {
			const statusValue = String(task.status).toLowerCase();
			isPending = statusValue.includes("pending");
		}

		debugLog(
			`Task: ${
				task.title || task.id || "Unknown"
			} - hasAssignedName: ${hasAssignedName}, status: ${
				task.status
			}, isPending: ${isPending}`
		);

		return hasAssignedName && isPending;
	});

	debugLog("PendingTasksCard - pendingTasks:", pendingTasks);
	const pendingTasksCount = pendingTasks.length;

        const handleClick = () => {
                navigate("/workplaner", { state: { filter: "pending" } });
        };

	return (
		<AnimalCard
			title={t("pending")}
			count={pendingTasksCount}
			status={pendingTasksCount === 0 ? "green" : "yellow"}
			onClick={handleClick}
			cardId={CARD_IDS.PENDING_TASKS}
		/>
	);
}

/**
 * Card displaying the count of in-progress work tasks
 */
export function InProgressTasksCard({ workTasks = [] }) {
        const { t } = useTranslation();
        const navigate = useNavigate();

	// Debug work tasks
	debugLog("InProgressTasksCard - workTasks count:", workTasks.length);

	// Filter tasks that are assigned and in-progress
	const inProgressTasks = workTasks.filter((task) => {
		// Check if task is an object
		if (typeof task !== "object" || task === null) {
			return false;
		}

		// Check for assignedToName property
		const hasAssignedName = Boolean(
			task.assignedToName || (task.assignedTo && task.assignedTo.name)
		);

		// Check various formats of 'in-progress' status
		let isInProgress = false;

		// Check for status property directly
		if ("status" in task) {
			const statusValue = String(task.status).toLowerCase();
			isInProgress =
				statusValue.includes("progress") || statusValue.includes("in-progress");
		}

		return hasAssignedName && isInProgress;
	});

	debugLog("InProgressTasksCard - inProgressTasks:", inProgressTasks);
	const inProgressTasksCount = inProgressTasks.length;

        const handleClick = () => {
                navigate("/workplaner", { state: { filter: "in-progress" } });
        };

	return (
		<AnimalCard
			title={t("in-progress")}
			count={inProgressTasksCount}
			status={inProgressTasksCount === 0 ? "green" : "yellow"}
			onClick={handleClick}
			cardId={CARD_IDS.IN_PROGRESS_TASKS}
		/>
	);
}

/**
 * Card displaying the count of completed work tasks
 */
export function CompletedTasksCard({ workTasks = [] }) {
        const { t } = useTranslation();
        const navigate = useNavigate();

	// Debug work tasks
	debugLog("CompletedTasksCard - workTasks count:", workTasks.length);

	// Filter tasks that are assigned and completed
	const completedTasks = workTasks.filter((task) => {
		// Check if task is an object
		if (typeof task !== "object" || task === null) {
			return false;
		}

		// Check for assignedToName property
		const hasAssignedName = Boolean(
			task.assignedToName || (task.assignedTo && task.assignedTo.name)
		);

		// Check various formats of 'completed' status
		let isCompleted = false;

		// Check for status property directly
		if ("status" in task) {
			const statusValue = String(task.status).toLowerCase();
			isCompleted =
				statusValue.includes("completed") || statusValue.includes("done");
		}

		return hasAssignedName && isCompleted;
	});

	debugLog("CompletedTasksCard - completedTasks:", completedTasks);
	const completedTasksCount = completedTasks.length;

        const handleClick = () => {
                navigate("/workplaner", { state: { filter: "completed" } });
        };

	return (
		<AnimalCard
			title={t("completed")}
			count={completedTasksCount}
			status="green"
			onClick={handleClick}
			cardId={CARD_IDS.COMPLETED_TASKS}
		/>
	);
}
